import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { CheckCircle, XCircle, Clock, Search, User, Building, Plus, X, Eye, EyeOff } from 'lucide-react';

const STATUS_LABELS = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
  aprovado: { label: 'Aprovado', color: 'bg-green-100 text-green-800' },
  rejeitado: { label: 'Rejeitado', color: 'bg-red-100 text-red-800' },
};

const EMPTY_FORM = { nome: '', email: '', senha: '', telefone: '', whatsapp: '', creci: '', cpf: '', tipo: 'autonomo', imobiliaria_nome: '', status: 'aprovado' };

function ModalCriarUsuario({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      await api.post('/usuarios-vendas', form);
      onSuccess();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">Novo Usuário de Vendas</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {erro && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{erro}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Nome completo *</label>
              <input required value={form.nome} onChange={e => set('nome', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="João da Silva" />
            </div>

            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">E-mail *</label>
              <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="joao@email.com" />
            </div>

            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Senha *</label>
              <div className="relative">
                <input required type={showSenha ? 'text' : 'password'} value={form.senha} onChange={e => set('senha', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Mínimo 6 caracteres" minLength={6} />
                <button type="button" onClick={() => setShowSenha(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showSenha ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">WhatsApp</label>
              <input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="(11) 99999-9999" />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Telefone</label>
              <input value={form.telefone} onChange={e => set('telefone', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="(11) 3333-4444" />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">CRECI</label>
              <input value={form.creci} onChange={e => set('creci', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="CRECI-SP 12345" />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">CPF</label>
              <input value={form.cpf} onChange={e => set('cpf', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="000.000.000-00" />
            </div>

            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Vínculo</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => set('tipo', 'autonomo')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${form.tipo === 'autonomo' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  Autônomo
                </button>
                <button type="button" onClick={() => set('tipo', 'imobiliaria')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${form.tipo === 'imobiliaria' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  Imobiliária
                </button>
              </div>
            </div>

            {form.tipo === 'imobiliaria' && (
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Nome da Imobiliária</label>
                <input value={form.imobiliaria_nome} onChange={e => set('imobiliaria_nome', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Imobiliária XYZ" />
              </div>
            )}

            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Status inicial</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => set('status', 'aprovado')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${form.status === 'aprovado' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  Aprovado
                </button>
                <button type="button" onClick={() => set('status', 'pendente')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${form.status === 'pendente' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  Pendente
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Criando...' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsuariosVendasList() {
  const qc = useQueryClient();
  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [search, setSearch] = useState('');
  const [confirmando, setConfirmando] = useState(null);
  const [modalCriar, setModalCriar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['usuarios-vendas', filtroStatus],
    queryFn: () => api.get('/usuarios-vendas', { params: { status: filtroStatus } }).then(r => r.data.data),
  });

  const aprovarMut = useMutation({
    mutationFn: (id) => api.patch(`/usuarios-vendas/${id}/aprovar`),
    onSuccess: () => { qc.invalidateQueries(['usuarios-vendas']); setConfirmando(null); },
  });

  const rejeitarMut = useMutation({
    mutationFn: (id) => api.patch(`/usuarios-vendas/${id}/rejeitar`),
    onSuccess: () => { qc.invalidateQueries(['usuarios-vendas']); setConfirmando(null); },
  });

  const rows = (data || []).filter(u =>
    !search || u.nome?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.creci?.toLowerCase().includes(search.toLowerCase())
  );

  const pendentes = (data || []).filter(u => u.status_corretor === 'pendente').length;

  return (
    <div>
      {modalCriar && (
        <ModalCriarUsuario
          onClose={() => setModalCriar(false)}
          onSuccess={() => { setModalCriar(false); qc.invalidateQueries(['usuarios-vendas']); }}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Usuários de Vendas</h1>
          <p className="text-sm text-gray-500 mt-1">Corretores cadastrados no Portal de Vendas</p>
        </div>
        <div className="flex items-center gap-3">
          {pendentes > 0 && (
            <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1 rounded-full flex items-center gap-2">
              <Clock size={14} /> {pendentes} aguardando aprovação
            </span>
          )}
          <button
            onClick={() => setModalCriar(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Plus size={16} /> Novo Usuário
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input-field pl-8 text-sm"
            placeholder="Buscar por nome, e-mail ou CRECI..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {['todos', 'pendente', 'aprovado', 'rejeitado'].map(s => (
            <button
              key={s}
              onClick={() => setFiltroStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filtroStatus === s ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === 'todos' ? 'Todos' : STATUS_LABELS[s]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            {filtroStatus === 'pendente' ? 'Nenhum cadastro aguardando aprovação.' : 'Nenhum usuário encontrado.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Corretor</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">CRECI / CPF</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Vínculo</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Contato</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Cadastro</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(u => {
                const st = STATUS_LABELS[u.status_corretor] || STATUS_LABELS.pendente;
                return (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User size={14} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{u.nome}</div>
                          <div className="text-xs text-gray-400">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{u.creci || '—'}</div>
                      <div className="text-xs text-gray-400">{u.cpf || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-600">
                        {u.tipo === 'imobiliaria' ? (
                          <><Building size={13} className="text-gray-400" />{u.imobiliaria_nome || 'Imobiliária'}</>
                        ) : (
                          <><User size={13} className="text-gray-400" />Autônomo</>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-600">{u.whatsapp || u.telefone || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${st.color}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.status_corretor === 'pendente' && (
                        confirmando === u.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Confirmar?</span>
                            <button
                              onClick={() => aprovarMut.mutate(u.id)}
                              disabled={aprovarMut.isPending}
                              className="text-xs bg-green-600 text-white px-2 py-1 rounded-lg hover:bg-green-700 flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Aprovar
                            </button>
                            <button
                              onClick={() => rejeitarMut.mutate(u.id)}
                              disabled={rejeitarMut.isPending}
                              className="text-xs bg-red-600 text-white px-2 py-1 rounded-lg hover:bg-red-700 flex items-center gap-1"
                            >
                              <XCircle size={12} /> Rejeitar
                            </button>
                            <button onClick={() => setConfirmando(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">✕</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmando(u.id)}
                            className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 font-medium"
                          >
                            Analisar
                          </button>
                        )
                      )}
                      {u.status_corretor === 'aprovado' && (
                        <button
                          onClick={() => rejeitarMut.mutate(u.id)}
                          disabled={rejeitarMut.isPending}
                          className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                        >
                          Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
